import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { calculateSectionTotals } from 'helpers/evaluations/calculateSectionTotals'
import { useAppSelector } from 'store/hooks'
import { slices } from 'store/slices'

const useValidations = () => {
  const { t } = useTranslation()
  const translationPrefix = 'administration.blocks.type.questionBankV2.buttons'
  const [tooltip, setTooltip] = useState('')
  const stage = useAppSelector(slices.questionBank.stage)
  const data = useAppSelector(slices.questionBank.data)

  const totalScore = useMemo(() => {
    let total = 0

    data.sections.forEach((section) => {
      const sectionResume = calculateSectionTotals(section)
      total += sectionResume.score
    })
    return total
  }, [data])

  const evaluationIsValid = useCallback(() => {
    if (stage === 0) {
      if (data.name.trim().length === 0) {
        setTooltip(t(`${translationPrefix}.tooltip.hasNoName`))
        return false
      }

      if (data.sections.length === 0) {
        setTooltip(t(`${translationPrefix}.tooltip.hasNoSections`))
        return false
      }

      if (data.sections.some((section) => section.questions.length === 0)) {
        setTooltip(t(`${translationPrefix}.tooltip.hasNoQuestions`))
        return false
      }

      if (
        data.sections.some(
          (section) => calculateSectionTotals(section).score < 1
        )
      ) {
        setTooltip(t(`${translationPrefix}.tooltip.hasNoQuestionsWithScore`))
        return false
      }
    } else {
      const minimumPassingScore =
        typeof data.minimumPassingScore === 'number'
          ? data.minimumPassingScore
          : 0
      if (minimumPassingScore < 1 || minimumPassingScore > totalScore) {
        setTooltip(t(`${translationPrefix}.tooltip.hasNoMinimumPassingScore`))
        return false
      }

      const maxRetries = data.maxRetries
      if (typeof maxRetries !== 'number' || maxRetries < 1) {
        setTooltip(
          t(`${translationPrefix}.tooltip.hasNoMaximumEvaluationAttempts`)
        )
        return false
      }
    }
    setTooltip('')
    return true
  }, [data, stage, t, totalScore])

  return { evaluationIsValid, tooltip }
}

export default useValidations
